import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, Output, Signal, SimpleChanges, ViewChild } from '@angular/core';
import { AuthService } from '../../services';
import { DriverAssignmentFilter, User } from '../../models';
import { UnifiedTreeItem } from '../../models/driver.model';
import { Observable, Subject, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { FormsModule } from '@angular/forms';
import { NgbModule, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { DriverVehicleAssetIconDirective } from '../../directives/driver-vehicle-asset-icon.directive';
import { AssignmentFilterService } from '../../../driver-management/driver-vehicle-assignment/services/assignment-filter.service';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-asset-assignment-filter',
  standalone: true,
  imports: [NgbModule, CommonModule, FormsModule, ScrollingModule, DriverVehicleAssetIconDirective, NgbPopover, TranslocoPipe],
  templateUrl: './asset-assignment-filter.component.html',
  styleUrl: './asset-assignment-filter.component.scss'
})
export class AssetAssignmentFilterComponent implements OnChanges {
  @Input() filter: DriverAssignmentFilter;
  @Input() accountFilterChanged$: Observable<number[]>;
  @Input() enableCollapsibleAccounts: boolean = false;
  @Input() tree: UnifiedTreeItem[] = [];
  @Input() entityType: 'driver' | 'asset' = 'driver';
  @Input() labelTitle: string = '';
  @Input() isBulkAssignment: boolean = false;
  @Input() isLoaded: Signal<boolean>;
  @Input() preSelectedIds: number[] = [];

  @Input() closeOnOutsideClick: boolean = false;

  @Input() enforceAssignmentRules: boolean = true;
  @Output() driverAssignmentFilterChanged: EventEmitter<DriverAssignmentFilter> = new EventEmitter<DriverAssignmentFilter>();

  private skipNextFilterReset = false;

  @ViewChild('dropdown') dropdown: ElementRef;
  @ViewChild('dropdownTrigger') dropdownButton: ElementRef;

  isLoading: boolean = false;
  driverName: string = '';
  searchTerm: string = '';

  user: User;
  driversTree: UnifiedTreeItem[] = [];
  filteredTree: UnifiedTreeItem[] = [];
  hierarchicalEntitiesTree: UnifiedTreeItem[] = [];
  selectedEntities: Array<UnifiedTreeItem> = [];
  selectedEntitiesCount: number = 0;
  selectedBillingAccountId: number | null;
  currentPopover: NgbPopover | null = null;
  closePopoverTimeout: any;
  isSingleAssignment: boolean = false;

  private cancelSubject: Subject<void> = new Subject<void>();
  private accountFilterSubscription: Subscription;

  constructor(
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private assignmentFilterService: AssignmentFilterService
  ) { }

  ngOnInit(): void {
    this.init();

    this.isSingleAssignment = this.assignmentFilterService.isSingleAssignment();
    if (this.tree && this.tree.length > 0) {
      this.isLoading = false;
      this.hierarchicalEntitiesTree = this.tree;
      this.filteredTree = this.flattenTreeWithCollapse(this.tree);
    } else {
      this.isLoading = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tree'] && this.tree) {
      this.isLoading = false;
      this.hierarchicalEntitiesTree = this.tree;
      this.filteredTree = this.flattenTreeWithCollapse(this.tree);

      if (this.preSelectedIds.length > 0) {
        this.applyPreSelectedIds();
      } else {
        this.resetEntities();
      }
    }

    if (changes['filter']) {
      if (this.skipNextFilterReset) {
        this.skipNextFilterReset = false;
      } else {
        this.resetEntities();
      }
    }
  }

  resetEntities() {
    this.selectedEntitiesCount = 0;
    this.selectedEntities = [];
    this.filteredTree = this.flattenTreeWithCollapse(this.tree);

    if (this.enforceAssignmentRules && !this.assignmentFilterService.isSingleAssignment()) {
      this.assignmentFilterService.setSelectedBillingAccountId(this.filteredTree)
    }

    setTimeout(() => {
      this.uncheckAllEntities(this.tree);
      this.cdr.detectChanges();
    });
  }

  async init(): Promise<void> {
    this.user = this.authService.getUser();
  }

  ngOnDestroy(): void {
    if (this.accountFilterSubscription) {
      this.accountFilterSubscription.unsubscribe();
    }

    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  openDropdown(event: Event): void {
    event.stopPropagation();
    if (!this.dropdown.nativeElement.className.includes('show')) {
      this.dropdownButton.nativeElement.click();
    }
  }

  isItemDisabled(item: UnifiedTreeItem): boolean {
    // An entity without a billing account is never selectable; the rest is the driver-assignment rule set.
    if (!this.enforceAssignmentRules) return item.billingAccountId === 0;

    return item.billingAccountId === 0 ||
      (this.assignmentFilterService.selectedBillingAccountId() !== null &&
        item.billingAccountId !== this.assignmentFilterService.selectedBillingAccountId());
  }

  shouldShowAssignmentIcon(item: UnifiedTreeItem): boolean {
    if (!this.enforceAssignmentRules) return false;
    if (this.entityType !== 'asset' && !this.assignmentFilterService.isSingleAssignment()) return false;
    return this.assignmentFilterService.isDriverAssignedToAsset(item.id);
  }

  shouldShowDocumentsWarning(item: UnifiedTreeItem): boolean {
    return this.entityType === 'driver' && item.hasExpiryOrMissingDocs!;
  }

  getAssignedDriverNames(assetId: number): string[] {
    if (this.assignmentFilterService.isSingleAssignment()) return [];

    const selectedDrivers = this.assignmentFilterService.selectedDrivers();
    const map = this.assignmentFilterService.driverToAssetsMap();
    return selectedDrivers
      .filter(driver => map.get(driver.id)?.includes(assetId))
      .map(driver => driver.name);
  }

  isVehicleAssigned(assetId: number): boolean {
    if (!this.enforceAssignmentRules || !this.assignmentFilterService.isSingleAssignment()) return false;
    return this.assignmentFilterService.isDriverAssignedToAsset(assetId);
  }

  async onCheckboxChange(item: UnifiedTreeItem & { checked?: boolean }): Promise<void> {
    this.toggleCheckBox(item);

    const selectedBillingAccountId = this.assignmentFilterService.selectedBillingAccountId();
    this.selectedEntities = this.getAllCheckedEntities(this.hierarchicalEntitiesTree);

    if (this.enforceAssignmentRules) {
      if (!this.assignmentFilterService.isSingleAssignment()) {
        this.assignmentFilterService.setSelectedBillingAccountId(this.selectedEntities);
      }

      if (this.assignmentFilterService.selectedBillingAccountId() !== selectedBillingAccountId) {
        await this.assignmentFilterService.updateAssignedAssetDrivers(this.cancelSubject);
      }
    }

    if (this.entityType === 'driver') {
      this.filter.drivers = this.selectedEntities.map(x => ({
        id: x.id,
        name: x.name,
        accountName: this.findParentAccountName(x, this.hierarchicalEntitiesTree)
      }));

      this.assignmentFilterService.setSelectedDrivers(this.filter.drivers);
    } else if (this.entityType === 'asset') {
      this.filter.assets = this.selectedEntities.map(x => ({
        id: x.id,
        name: x.name,
        accountName: this.findParentAccountName(x, this.hierarchicalEntitiesTree)
      }));
    }

    this.driverAssignmentFilterChanged.emit(this.filter);
    this.selectedEntitiesCount = this.selectedEntities.length;
  }

  searchEntities(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value;
    if (this.searchTerm) {
      this.filteredTree = this.searchTreeItems(this.hierarchicalEntitiesTree, this.searchTerm);
    } else {
      this.filteredTree = this.flattenTreeWithCollapse(this.hierarchicalEntitiesTree);
    }
  }

  searchTreeItems(items: UnifiedTreeItem[], searchTerm: string, level: number = 0): UnifiedTreeItem[] {
    let result: UnifiedTreeItem[] = [];

    for (const item of items) {
      const matched = item.name.toLowerCase().includes(searchTerm.toLowerCase());
      let childResults: UnifiedTreeItem[] = [];

      if (item.children && item.children.length > 0) {
        childResults = this.searchTreeItems(item.children, searchTerm, level + 1);
      }

      if (matched) {
        item.hierarchyLevel = level;
        result.push(item);

        if (item.children && item.children.length) {
          result.push(...this.flattenTreeWithCollapse(item.children, level + 1));
        }
      } else if (childResults.length > 0) {
        item.hierarchyLevel = level;
        result.push(item);
        result.push(...childResults);
      }
    }

    return result;
  }

  findNodeInTree(id: number, tree: UnifiedTreeItem[]): UnifiedTreeItem | null {
    for (const node of tree) {
      if (node.id === id) {
        return node;
      }

      if (node.children && node.children.length) {
        const found = this.findNodeInTree(id, node.children);
        if (found) {
          return found;
        }
      }
    }

    return null
  }

  getTotalEntities(item: UnifiedTreeItem, visited = new Set<number>()): number {
    // Prevent cycles
    if (visited.has(item.id)) {
      return 0;
    }

    visited.add(item.id);
    if (item.type === this.entityType) {
      return 1;
    }

    if (!item.children || item.children.length === 0) {
      return 0;
    }

    return item.children.reduce((sum, child) => sum + this.getTotalEntities(child, visited), 0);
  }

  private toggleCheckBox(item: UnifiedTreeItem) {
    const isChecked = !item.checked;
    item.checked = isChecked;

    this.setCheckedRecursive(item, isChecked);
  }

  private setCheckedRecursive(item: UnifiedTreeItem, checked: boolean): void {
    if (item.children && item.children.length) {
      for (const child of item.children) {
        if (!this.isVehicleAssigned(child.id)) {
          child.checked = checked;
        }
        this.setCheckedRecursive(child, checked);
      }
    }
  }

  private getAllCheckedEntities(tree: UnifiedTreeItem[]): UnifiedTreeItem[] {
    let result: UnifiedTreeItem[] = [];
    for (const item of tree) {
      if (item.type === this.entityType && item.checked) {
        result.push(item);
      }
      if (item.children && item.children.length) {
        result = result.concat(this.getAllCheckedEntities(item.children));
      }
    }

    return result;
  }

  private applyPreSelectedIds(): void {
    const idSet = new Set(this.preSelectedIds);
    this.checkByIds(this.hierarchicalEntitiesTree, idSet);

    this.selectedEntities = this.getAllCheckedEntities(this.hierarchicalEntitiesTree);
    this.selectedEntitiesCount = this.selectedEntities.length;

    if (this.entityType === 'asset') {
      this.filter.assets = this.selectedEntities.map(x => ({
        id: x.id,
        name: x.name,
        accountName: this.findParentAccountName(x, this.hierarchicalEntitiesTree)
      }));
    } else if (this.entityType === 'driver') {
      this.filter.drivers = this.selectedEntities.map(x => ({
        id: x.id,
        name: x.name,
        accountName: this.findParentAccountName(x, this.hierarchicalEntitiesTree)
      }));
    }

    this.skipNextFilterReset = true;
    this.driverAssignmentFilterChanged.emit(this.filter);
    this.filteredTree = this.flattenTreeWithCollapse(this.hierarchicalEntitiesTree);
    this.cdr.detectChanges();
  }

  private checkByIds(tree: UnifiedTreeItem[], ids: Set<number>): void {
    for (const item of tree) {
      if (ids.has(item.id) && item.type === this.entityType) {
        item.checked = true;
      }
      if (item.children?.length) {
        this.checkByIds(item.children, ids);
      }
    }
  }

  private uncheckAllEntities(tree: UnifiedTreeItem[]) {
    for (const item of tree) {
      item.checked = false;

      if (item.children && item.children.length) {
        this.uncheckAllEntities(item.children);
      }
    }
  }

  private findParentAccountName(item: UnifiedTreeItem, tree: UnifiedTreeItem[]): string | undefined {
    let result: string | undefined;
    function traverse(nodes: UnifiedTreeItem[], parentAccountName?: string) {
      for (const node of nodes) {
        if (node.id === item.id) {
          result = parentAccountName;
          return
        }
        if (node.children && node.children.length) {
          traverse(node.children, node.type === 'account' ? node.name : parentAccountName)
        }
      }
    }

    traverse(tree);
    return result;
  }

  toggleCollapse(item: UnifiedTreeItem): void {
    item.collapsed = !item.collapsed;
    this.filteredTree = this.flattenTreeWithCollapse(this.hierarchicalEntitiesTree);
  }

  flattenTreeWithCollapse(tree: UnifiedTreeItem[], level: number = 0): UnifiedTreeItem[] {
    const result: UnifiedTreeItem[] = [];
    for (const item of tree) {
      // Set hierarchy level for indentation
      item.hierarchyLevel = level;
      // Set isParent based on children
      item.isParent = !!(item.children && item.children.length);

      result.push(item);

      // If collapsed, do not add children
      if (item.isParent && item.collapsed) {
        continue;
      }

      // Recursively add children
      if (item.children && item.children.length) {
        result.push(...this.flattenTreeWithCollapse(item.children, level + 1));
      }
    }

    return result;
  }

  openPopover(popover: NgbPopover) {
    if (this.assignmentFilterService.isSingleAssignment()) return;
    this.cancelClosePopover();

    if (this.currentPopover && this.currentPopover !== popover) {
      this.currentPopover.close();
    }

    this.currentPopover = popover;
    popover.open();
  }

  scheduleClosePopover(popover: NgbPopover) {
    this.closePopoverTimeout = setTimeout(() => {
      popover.close();
      this.currentPopover = null;
    }, 500);
  }

  cancelClosePopover() {
    if (this.closePopoverTimeout) {
      clearTimeout(this.closePopoverTimeout);
      this.closePopoverTimeout = null;
    }
  }
}